import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LangContext } from '../App';
import { translations } from '../lib/translations';

const MONOGRAM_URL =
  'https://customer-assets.emergentagent.com/job_bbc14d05-1309-4872-bdcf-991a413c9c8e/artifacts/9i1h196a_JCS_monograma_transparente.png';

export default function Navbar({ onLangChange }) {
  const lang = useContext(LangContext);
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const navLinks = [
    { key: 'about', id: 'about' },
    { key: 'areas', id: 'areas' },
    { key: 'partners', id: 'partners' },
    { key: 'contact', id: 'contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#FDFBF7]/95 shadow-sm border-b border-[#E5E0D8]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => scrollTo('home')}
          className="flex items-center"
          data-testid="nav-logo"
          aria-label="Início"
        >
          <img src={MONOGRAM_URL} alt="JCS Logo" className="h-10 w-auto" />
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map(({ key, id }) => (
            <button
              key={key}
              onClick={() => scrollTo(id)}
              data-testid={`nav-link-${id}`}
              className="font-sans text-xs tracking-[0.25em] uppercase text-[#4A4A4A] hover:text-[#6B2D3E] transition-colors duration-300"
            >
              {t[key]}
            </button>
          ))}
        </div>

        {/* Lang Toggle + Hamburger */}
        <div className="flex items-center gap-5">
          <div
            className="flex items-center"
            data-testid="lang-toggle"
            role="group"
            aria-label="Language selector"
          >
            <button
              onClick={() => onLangChange('PT')}
              data-testid="lang-btn-pt"
              className={`font-sans text-xs tracking-widest uppercase px-2 py-1 transition-all duration-300 ${
                lang === 'PT'
                  ? 'text-[#6B2D3E] font-semibold'
                  : 'text-[#AAAAAA] hover:text-[#6B2D3E]'
              }`}
            >
              PT
            </button>
            <span className="text-[#D5D0C8] text-xs select-none">|</span>
            <button
              onClick={() => onLangChange('EN')}
              data-testid="lang-btn-en"
              className={`font-sans text-xs tracking-widest uppercase px-2 py-1 transition-all duration-300 ${
                lang === 'EN'
                  ? 'text-[#6B2D3E] font-semibold'
                  : 'text-[#AAAAAA] hover:text-[#6B2D3E]'
              }`}
            >
              EN
            </button>
          </div>

          <button
            className="md:hidden text-[#4A4A4A] hover:text-[#6B2D3E] transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-[#FDFBF7] border-b border-[#E5E0D8]"
            data-testid="mobile-menu"
          >
            <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col gap-5">
              {navLinks.map(({ key, id }) => (
                <button
                  key={key}
                  onClick={() => scrollTo(id)}
                  data-testid={`mobile-nav-link-${id}`}
                  className="font-sans text-xs tracking-[0.25em] uppercase text-[#4A4A4A] hover:text-[#6B2D3E] transition-colors text-left py-1"
                >
                  {t[key]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
